import { gql } from '@apollo/client';

export const GET_ADD_ONS = gql`
  query getAddOns($clientId: ID!) {
    addOns(clientId: $clientId) {
      id
      name
    }
  }
`;

export const GET_DATA_ENTRIES = gql`
  query getDataEntries($clientId: ID!, $startDate: String!, $endDate: String!) {
    dataEntries(clientId: $clientId, fromDate: $startDate, toDate: $endDate) {
      id
      amountInCents
      journalEntry {
        id
      }
      ledgerAccount {
        id
      }
    }
  }
`;

export const GET_JOURNAL_ENTRIES = gql`
  query getJournalEntries($clientId: ID!, $startDate: String!, $endDate: String!) {
    journalEntries(clientId: $clientId, fromDate: $startDate, toDate: $endDate) {
      id
      addOn {
        id
        name
      }
      categorization {
        id
      }
      clientCatLedger {
        id
      }
      clientCatResponse
      clientComments {
        id
        body
      }
      dateOfTransaction
      document {
        id
      }
      internalNotes {
        id
        body
        flagged
      }
      relatedJE {
        id
      }
      vendorName
    }
  }
`;

export const GET_LEDGER_ACCOUNTS = gql`
  query getLedgerAccounts($clientId: ID!) {
    ledgerAccounts(clientId: $clientId) {
      id
      active
      category {
        id
      }
      code
      details
      internalNotes {
        id
        body
      }
      name
      order
      role
    }
  }
`;

export const GET_LEDGER_ACCOUNT_CATEGORY_ORDERS = gql`
  query getLedgerAccountCategoryOrders($clientId: ID!) {
    ledgerAccountCategoryOrders(clientId: $clientId) {
      id
      ledgerAccountCategory {
        id
        name
      }
      ledgerCategoryOrderingType
    }
  }
`;

export const GET_BALANCE_SHEET_REPORT = gql`
  query getBalanceSheetReport($clientId: ID!, $date: String!) {
    balanceSheetReport(clientId: $clientId, date: $date)
      @rest(type: "BalanceSheetReport", path: "balance-sheet/json/:clientId/:date") {
      balanceSheetLedgerSummaries {
        balanceInCents
        ledgerAccount {
          id
        }
      }
      balanceSheetCategorySummaries {
        category {
          id
        }
        balanceInCents
      }
    }
  }
`;

export const GET_INCOME_STATEMENT_REPORT = gql`
  query getIncomeStatementReport($clientId: ID!, $fromDate: String!, $toDate: String!) {
    incomeStatementReport(clientId: $clientId, fromDate: $fromDate, toDate: $toDate)
      @rest(
        type: "IncomeStatementReport"
        path: "income-statement/json/:clientId/:fromDate/:toDate"
      ) {
      incomeStatementLedgerSummaries {
        ledgerAccount {
          id
        }
        sumTotalInCents
      }
      incomeStatementCategorySummaries {
        category {
          id
        }
        sumTotalInCents
      }
    }
  }
`;

export const GET_TRANSACTION_CATEGORIZATIONS = gql`
  query getTransactionCategorizations($clientId: ID!, $startDate: String!, $endDate: String!) {
    transactionCategorizations(clientId: $clientId, fromDate: $startDate, toDate: $endDate) {
      id
      suggestedLedgers {
        id
        ledgerAccount {
          id
        }
      }
    }
  }
`;

export const ASSIGN_DATA_ENTRY_LEDGER_ACCOUNT = gql`
  mutation assignDataEntryLedgerAccount($updateDataEntriesInput: UpdateDataEntriesInput!) {
    updateDataEntries(updateDataEntriesInput: $updateDataEntriesInput) {
      dataEntries {
        id
        ledgerAccount {
          id
        }
      }
    }
  }
`;

export const UPDATE_TRANSACTION_CATEGORIZATION = gql`
  mutation updateTransactionCategorization(
    $journalEntryId: ID!
    $transactionCategorizationInput: TransactionCategorizationInput!
  ) {
    updateTransactionCategorization(
      journalEntryId: $journalEntryId
      transactionCategorizationInput: $transactionCategorizationInput
    ) {
      id
      suggestedLedgers {
        id
        ledgerAccount {
          id
        }
      }
    }
  }
`;

export const GET_CATEGORIZATION_INCONSISTENCIES = gql`
  query getCategorizationInconsistencies($clientId: ID!, $startDate: String!, $endDate: String!) {
    categorizationInconsistencies(clientId: $clientId, fromDate: $startDate, toDate: $endDate) {
      dataEntryIds
      vendorName
    }
  }
`;

export const GET_CATEGORIZATION_LEDGER_ACCOUNTS = gql`
  query getCategorizationLedgerAccounts($clientId: ID!) {
    ledgerAccounts(clientId: $clientId) {
      id
      active
      name
    }
  }
`;

export const CLIENT_YEARLY_DATA = gql`
  query ClientYearlyData($clientId: ID!) {
    client(id: $clientId) {
      id
      clientDetails: more {
        structure
        industry
      }
      yearlyData {
        year
        trialBalanceOrPriorReturn
        cpaAdjustment
        equityClosingAdjustment
        yearEndReview
        yearEndFinancialPackage
      }
    }
  }
`;

export const CREATE_CLIENT_YEARLY_DATA = gql`
  mutation CreateYearlyData($clientId: ID!, $yearlyData: CreateOrUpdateYearlyData!) {
    createYearlyData(clientId: $clientId, yearlyData: $yearlyData) {
      year
    }
  }
`;

export const UPDATE_CLIENT_YEARLY_DATA = gql`
  mutation UpdateYearlyData($clientId: ID!, $yearlyData: CreateOrUpdateYearlyData!) {
    updateYearlyData(clientId: $clientId, yearlyData: $yearlyData) {
      year
    }
  }
`;

export const GET_FISCAL_YEAR_END = gql`
  query getFiscalYearEnd($clientId: ID!) {
    client(id: $clientId) {
      id
      fiscalYearEnd
    }
  }
`;

export const GET_JOURNAL_ENTRIES_WITH_OUTSTANDING_COMMENTS = gql`
  query journalEntriesWithOutstandingComments($clientId: ID!) {
    journalEntriesWithOutstandingComments(clientId: $clientId) {
      id
      journalEntries {
        id
        dateOfTransaction
        vendorName
        dataEntries {
          id
          amountInCents
          ledgerAccount {
            id
          }
        }
        clientComments {
          id
          author {
            id
          }
          body
          createdOn
        }
        addOn {
          id
        }
        document {
          id
        }
        categorization {
          id
          categorizationUIClientComment
          selectedLedgerAccount {
            id
          }
        }
      }
    }
  }
`;

export const CLEAR_JOURNAL_ENTRY_OUTSTANDING_COMMENTS = gql`
  mutation clearJournalEntryOutstandingComments($journalEntryIds: [String!], $clientId: ID!) {
    clearJournalEntryOutstandingCommentsNotification(
      journalEntryIds: $journalEntryIds
      clientId: $clientId
    ) {
      id
      journalEntries {
        id
      }
    }
  }
`;

const DATA_ENTRY_FRAGMENT = gql`
  fragment DataEntryFragment on DataEntry {
    id
    amount
    journalEntry {
      vendorName
      dateOfTransaction
      id
      classification {
        id
        value
        state
      }
    }
    ledgerAccount {
      id
      name
    }
  }
`;

export const GET_PERSONAL_BUSINESS_UNCLASSIFIED_DETAILS = gql`
  ${DATA_ENTRY_FRAGMENT}
  query getPersonalBusinessDetails(
    $clientId: ID!
    $startDate: RFC3339Date!
    $endDate: RFC3339Date!
    $after: ID
    $sort: DataEntriesSort
    $ledgerAccountIds: [ID!]
    $query: String
    $type: JournalEntryClassificationValue
  ) {
    personalBusinessClassificationDetailsByClient(clientId: $clientId) {
      unclassifiedEntriesCount
      classifiedEntriesCount
      dataEntries(
        filter: {
          query: $query
          startDate: $startDate
          endDate: $endDate
          ledgerAccountIds: $ledgerAccountIds
          isClassified: false
          classificationValue: $type
          pagination: { limit: 2500, after: $after, sort: $sort }
        }
      ) {
        searchMarker
        pageInfo {
          total
          hasNextPage
          endCursor
        }
        dataEntries {
          ...DataEntryFragment
        }
      }
      ledgerAccounts {
        id
        name
      }
    }
  }
`;

export const GET_PERSONAL_BUSINESS_COUNTS_ONLY = gql`
  query getPersonalBusinessCounts($clientId: ID!) {
    personalBusinessClassificationDetailsByClient(clientId: $clientId) {
      unclassifiedEntriesCount
    }
  }
`;
